/**
 * じっくり最適化ジョブの状態を追う
 *
 * 結果は裏で日プランへ書き込まれるので、画面は状態だけを見に行き、終わったらプランを読み直す。
 * 画面を閉じていてもプッシュ通知が届くので、ここでの見張りは開いているあいだだけでよい。
 */

import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { getDeepSplit, startDeepSplit } from '@/api/services/route-plan'
import type { DeepOptimizeJob, SplitPlanDaysParams } from '@/api/types/route-plan'
import { parseUtcDate } from '@/lib/date-utils'

const POLL_INTERVAL_MS = 10_000

const isActiveJob = (job: DeepOptimizeJob | null): boolean => job?.status === 'pending' || job?.status === 'running'

/** 探索時間が尽きる時刻。実際はその後の保存ぶんだけ遅れるので「ごろ」と添えて出す */
const finishLabelOf = (job: DeepOptimizeJob): string =>
  new Date(parseUtcDate(job.started_at ?? job.created_at).getTime() + job.time_limit_seconds * 1000).toLocaleTimeString(
    'ja-JP',
    { hour: '2-digit', minute: '2-digit' },
  )

type Args = {
  planId: number
  /** 共有先や別画面のあいだは見に行かない */
  enabled: boolean
  /** 終わったときにプランを読み直す */
  onDone: () => unknown
}

export const useDeepOptimizeJob = ({ planId, enabled, onDone }: Args) => {
  const [job, setJob] = useState<DeepOptimizeJob | null>(null)
  const previous = useRef<DeepOptimizeJob | null>(null)
  const isActive = isActiveJob(job)

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    getDeepSplit(planId, controller.signal)
      .then(setJob)
      .catch(() => {
        // 状態が分からなくてもプランは使えるので黙って通す
      })
    return () => controller.abort()
  }, [enabled, planId])

  useEffect(() => {
    if (!enabled || !isActive) return
    const timer = window.setInterval(() => {
      getDeepSplit(planId)
        .then(setJob)
        .catch(() => {
          // 一時的な通信失敗は次の周期で取り直す
        })
    }, POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [enabled, isActive, planId])

  // 見ているあいだに終わったときだけ知らせる。開いた時点で終わっていたものは通知で伝わっている
  useEffect(() => {
    const before = previous.current
    previous.current = job
    if (!isActiveJob(before) || job === null || job.id !== before?.id) return
    if (job.status === 'done') {
      const rest = job.skipped_count ? `。${job.skipped_count} 件は入りきりませんでした` : ''
      toast.success(`じっくり最適化が終わりました。${job.day_plans.length} 日に分けました${rest}`, {
        description: job.day_plans.map((day) => day.title).join(' / '),
      })
      void onDone()
    }
    if (job.status === 'failed') toast.error(job.error ?? 'じっくり最適化に失敗しました')
  }, [job, onDone])

  const start = async (params: SplitPlanDaysParams) => {
    setJob(await startDeepSplit(planId, params))
  }

  return { job, isActive, finishLabel: job ? finishLabelOf(job) : null, start }
}
